import { useSession } from 'next-auth/react';
import {
  Button,
  Flex,
  FormControl,
  Input,
  Text,
} from '@eqi-investimentos/react';
import { Controller, SubmitHandler, useWatch } from 'react-hook-form';
import { useT } from '@/libs/i18n/utils/useT';
import { Drawer } from '@/components/modules/Drawer';
import { PasswordRequirementsChecklist } from '@/components/modules/PasswordRequirementsChecklist';
import { BadgeErrorForm } from '@/modules/Auth/ui/BadgeErrorForm';
import { useUpdatePassword } from '@/modules/Customer/http/client/mutations/useUpdatePassword';
import { ResetPasswordForm, useResetPassword } from '@/hooks/useResetPassword';
import { EDIT_PROFILE_INFO_DRAWER_IDS } from '../utils';
import { IfRender } from '@/utils/jsx';
import { OTPConfirmation } from './OTPConfirmation';
import { SuccessMessage } from './SuccessMessage';

export const EditPasswordDrawer = () => {
  const t = useT();
  const { data: session } = useSession();

  const {
    handleSendOTP,
    validateOTP,
    isSendOtpError,
    isSendOtpPending,
    isOtpInvalid,
    step,
    setStep,
    resetOtp,
    control,
    errorReset,
    setErrorReset,
    handleSubmit,
    reset,
  } = useResetPassword(session);

  const {
    mutateAsync: updatePassword,
    isPending: isUpdatingPassword,
    isSuccess,
  } = useUpdatePassword();

  const password = useWatch({ control, name: 'password' });

  const resetPasswordEdition = () => {
    reset();
    setStep(1);
  };

  const onSubmit: SubmitHandler<ResetPasswordForm> = async ({ password }) => {
    try {
      await updatePassword({
        newPassword: password,
        token: resetOtp,
      });
    } catch {
      setErrorReset('Erro ao resetar senha. Por favor, tente novamente.');
    }
  };

  const onClose = () => {
    window.dispatchEvent(
      new CustomEvent(
        `close-chakra-modal-${EDIT_PROFILE_INFO_DRAWER_IDS.password}`
      )
    );
  };

  return (
    <Drawer
      id={EDIT_PROFILE_INFO_DRAWER_IDS.password}
      headerContent={
        isSuccess ? undefined : (
          <Text size="2xl" color="content.primary">
            {t('drawers.personalInfo.updatePassword.header')}
          </Text>
        )
      }
      closeButtonProps={{ top: '3.125rem' }}
      headerProps={{
        pl: '3.4375rem',
        pr: '3rem',
        py: '2.8125rem',
      }}
      bodyProps={{ px: '2xl' }}
      drawerProps={{ closeOnEsc: !isSuccess, closeOnOverlayClick: !isSuccess }}
      showCloseButton={!isSuccess}
      handleBeforeDrawerOpen={() => handleSendOTP('email', true)}
      handleBeforeDrawerClose={resetPasswordEdition}
    >
      <Flex flexDir="column" h="full" justify="space-between">
        {isSuccess && <SuccessMessage resetFlow={resetPasswordEdition} />}
        {!isSuccess && (
          <OTPConfirmation
            type="password"
            isNewValueSet={step === 1}
            resendOTP={() => handleSendOTP('email', true)}
            handleOTPValidation={validateOTP}
            isError={isOtpInvalid}
          >
            <Flex
              as="form"
              flexDir="column"
              gap="xl"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormControl
                label={t('drawers.personalInfo.updatePassword.password.label')}
                helperText={t(
                  'drawers.personalInfo.updatePassword.password.helperText'
                )}
                variant="fixed"
                isRequired
              >
                <Controller
                  name="password"
                  control={control}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  render={({ field: { ref, ...props } }) => (
                    <Input
                      placeholder={t(
                        'drawers.personalInfo.updatePassword.password.placeholder'
                      )}
                      type="password"
                      {...props}
                    />
                  )}
                />
              </FormControl>
              <PasswordRequirementsChecklist password={password ?? ''} />
              <FormControl
                label={t(
                  'drawers.personalInfo.updatePassword.confirmPassword.label'
                )}
                helperText={t(
                  'drawers.personalInfo.updatePassword.confirmPassword.helperText'
                )}
                variant="fixed"
                isRequired
              >
                <Controller
                  name="confirmPassword"
                  control={control}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  render={({ field: { ref, ...props } }) => (
                    <Input
                      placeholder={t(
                        'drawers.personalInfo.updatePassword.confirmPassword.placeholder'
                      )}
                      type="password"
                      {...props}
                    />
                  )}
                />
              </FormControl>
              <IfRender condition={!!errorReset}>
                <Flex justifyContent="center" w="full">
                  <BadgeErrorForm htmlError={errorReset ?? ''} />
                </Flex>
              </IfRender>
              <Flex flexDir="column" gap="m" w="full">
                <Button
                  isLoading={isUpdatingPassword || isSendOtpPending}
                  type="submit"
                >
                  {t('drawers.personalInfo.buttons.save')}
                </Button>
                <Button variant="outline" onClick={onClose}>
                  {t('drawers.personalInfo.buttons.cancel')}
                </Button>
                {isSendOtpError && (
                  <Text size="sm" lineHeight="1rem" color="alert.error">
                    Erro ao enviar o código. Por favor, aguarde um momento e
                    tente novamente!
                  </Text>
                )}
              </Flex>
            </Flex>
          </OTPConfirmation>
        )}
      </Flex>
    </Drawer>
  );
};
